import { httpsCallable } from 'firebase/functions';
import { functions, db, auth } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
  getVehicleAlerts,
  getVehicleScore,
  getFleetHealth,
} from '../utils/maintenanceRules';
import { honorificSpoken } from '../utils/honorifics';
import { snapshotMigrationConfig } from '../utils/migrationConfig';
import { fetchVehicles, fetchFleetRecords } from '../api/vehicles';
import { fetchOrders } from '../api/orders';
import { EMERGENCIAS_ACTIVAS as ASIST } from '../utils/featureFlags';

// Llama al Cloud Function proxy 'assistant' — la API key de Gemini nunca sale del servidor.
const callAssistant = httpsCallable(functions, 'assistant');

/** ¿Los pedidos ya viven en Postgres? Ante cualquier duda, el camino viejo. */
async function pedidosEnPostgres() {
  try {
    return (await snapshotMigrationConfig()).readsFrom('orders') === 'postgres';
  } catch (_) {
    return false;
  }
}

// Carga vehículos + registros de mantenimiento del usuario y arma el contexto
// compacto que el modelo usa para desambiguar "mi carro".
export async function buildAssistantContext(profile) {
  const uid = auth.currentUser?.uid;
  const empty = { vehicles: [], alertsByVehicle: {}, recordsByVehicle: {}, profile: profile || null };
  if (!uid) return empty;

  // 🔴 ESTO ES TODO LO QUE EL ASISTENTE SABE DEL CARRO DE QUIEN LE HABLA. Leído
  // de la base que ya no se escribe, el contexto sale vacío y el modelo no dice
  // «no tengo datos»: contesta igual, con seguridad, sobre una flota que no
  // existe. Un asistente que se inventa el carro es peor que uno que no está.
  const fuente = await snapshotMigrationConfig()
    .then(s => s.readsFrom('vehicles'))
    .catch(() => 'firestore');

  const vehicles = fuente === 'postgres'
    ? (await fetchVehicles({ page: 1 })).vehicles
    : (await getDocs(query(collection(db, 'vehicles'), where('userId', '==', uid)))).docs
        .map(d => ({ id: d.id, ...d.data() }));
  const recordsByVehicle = {};
  const alertsByVehicle = {};

  if (vehicles.length) {
    let records;
    if (fuente === 'postgres') {
      records = await fetchFleetRecords(vehicles);
    } else {
      const ids = vehicles.map(v => v.id);
      const chunks = [];
      for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10)); // 'in' admite máx 10
      const snaps = await Promise.all(chunks.map(c =>
        getDocs(query(collection(db, 'maintenanceRecords'), where('vehicleId', 'in', c)))
      ));
      records = snaps.flatMap(s => s.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    vehicles.forEach(v => {
      const recs = records.filter(r => r.vehicleId === v.id);
      recordsByVehicle[v.id] = recs;
      alertsByVehicle[v.id] = getVehicleAlerts(v, recs);
    });
  }

  // Citas y pedidos del usuario (cada uno en su try para no romper el contexto
  // si las reglas niegan algo — patrón seguro del proyecto).
  let appointments = [];
  try {
    const aSnap = await getDocs(query(collection(db, 'appointments'), where('clientId', '==', uid)));
    appointments = aSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (_) {}
  let orders = [];
  try {
    // 🔴 SIN INTERRUPTOR, EL ASISTENTE LE DICE AL CLIENTE QUE NO TIENE PEDIDOS.
    // Este bloque es el contexto que se le manda a Gemini: si lee la base vieja
    // con `orders` ya encendido, no falla nada — el modelo simplemente afirma,
    // con toda seguridad, algo que no es verdad sobre las compras del cliente.
    //
    // Solo hacen falta los más recientes (el contexto se queda con 6) y el API
    // ya ordena por fecha descendente, así que la primera página sobra.
    // `scope=client` lo pone `fetchOrders`: sin él, el dueño de un taller
    // recibiría en su contexto lo que le HAN comprado.
    if (await pedidosEnPostgres()) {
      orders = (await fetchOrders({ page: 1 })).orders;
    } else {
      const oSnap = await getDocs(query(collection(db, 'orders'), where('clientId', '==', uid)));
      orders = oSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    }
  } catch (_) {}

  return { vehicles, alertsByVehicle, recordsByVehicle, appointments, orders, profile: profile || null };
}

const fmtD = (ts) => {
  try { const d = ts?.toDate?.() || (ts ? new Date(ts) : null); return d ? d.toLocaleDateString('es-DO') : '?'; }
  catch (_) { return '?'; }
};
const ms = (ts) => { try { return ts?.toDate?.()?.getTime() || (ts ? new Date(ts).getTime() : 0); } catch (_) { return 0; } };

// Bloque de datos del usuario (perfil, vehículos, citas, pedidos, documentos,
// servicios, gastos). Lo usa el chat (system prompt) y el modo de VOZ en vivo.
// Saludo según la hora LOCAL del usuario (el modelo no sabe la hora).
function timeGreeting() {
  const now = new Date();
  const h = now.getHours();
  const hhmm = now.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' });
  const saludo = h < 6 ? 'buenas noches' : h < 12 ? 'buenos días' : h < 19 ? 'buenas tardes' : 'buenas noches';
  const franja = h < 6 ? 'madrugada, aún no ha salido el sol' : h < 12 ? 'mañana' : h < 19 ? 'tarde' : 'noche';
  return `${hhmm} (${franja}). Salúdalo con "${saludo}", nunca con un saludo que no corresponda a la hora.`;
}

export function buildContextBlock(ctx) {
  const vehicleLines = (ctx.vehicles || []).map(v => {
    const score = getVehicleScore(ctx.alertsByVehicle[v.id] || []);
    const alerts = (ctx.alertsByVehicle[v.id] || []).map(a => a.message).join('; ') || 'sin alertas';
    const plate = v.plate ? ` | placa ${v.plate}` : '';
    return `- id=${v.id} | ${v.brand} ${v.model} ${v.year || ''} | ${(v.currentMileage || 0).toLocaleString()} km${plate} | salud ${score}/100 | alertas: ${alerts}`;
  }).join('\n');
  const fleet = getFleetHealth((ctx.vehicles || []).map(v => ctx.alertsByVehicle[v.id] || []));
  const p = ctx.profile || {};
  const userBits = [
    p.fullName && `Nombre: ${p.fullName}`,
    honorificSpoken(p.honorific) && `Tratamiento preferido: "${honorificSpoken(p.honorific)}" (dirígete al usuario así; al HABLAR di siempre la palabra completa, ej. "ingeniero", "doctor", NUNCA la abreviatura)`,
    (p.city || p.address) && `Ubicación: ${p.city || p.address}`,
    p.phone && `Teléfono: ${p.phone}`,
  ].filter(Boolean).join(' | ');
  const citas = (ctx.appointments || [])
    .filter(a => a.status === 'pending' || a.status === 'confirmed')
    .sort((a, b) => ms(a.requestedDate) - ms(b.requestedDate)).slice(0, 6)
    .map(a => `- ${fmtD(a.requestedDate)} | ${a.mechanicName || 'taller'} | ${a.vehicleName || ''} | ${a.status} | ${a.issue || ''}`)
    .join('\n') || 'Sin citas próximas.';
  const pedidos = (ctx.orders || [])
    .slice().sort((a, b) => ms(b.createdAt) - ms(a.createdAt)).slice(0, 6)
    .map(o => `- ${fmtD(o.createdAt)} | RD$${(o.total || 0).toLocaleString()} | ${o.status}${Array.isArray(o.items) ? ` | ${o.items.length} art.` : ''}`)
    .join('\n') || 'Sin pedidos.';
  const documentos = (ctx.vehicles || []).map(v => {
    const parts = Object.entries(v.documents || {})
      .map(([k, d]) => d?.expiresAt ? `${k}: vence ${fmtD(d.expiresAt)}` : null).filter(Boolean);
    return parts.length ? `- ${v.brand} ${v.model}: ${parts.join('; ')}` : null;
  }).filter(Boolean).join('\n') || 'Sin documentos registrados.';
  const servicios = (ctx.vehicles || []).map(v => {
    const recs = (ctx.recordsByVehicle[v.id] || [])
      .slice().sort((a, b) => ms(b.serviceDate) - ms(a.serviceDate)).slice(0, 5)
      .map(r => `${r.serviceType} (${fmtD(r.serviceDate)}${r.cost ? `, RD$${Number(r.cost).toLocaleString()}` : ''})`);
    return recs.length ? `- ${v.brand} ${v.model}: ${recs.join('; ')}` : null;
  }).filter(Boolean).join('\n') || 'Sin servicios registrados.';
  const gastos = (ctx.vehicles || []).map(v => {
    const recs = ctx.recordsByVehicle[v.id] || [];
    const total = recs.reduce((s, r) => s + (Number(r.cost) || 0), 0);
    return total ? `- ${v.brand} ${v.model}: RD$${total.toLocaleString()} en ${recs.length} servicios` : null;
  }).filter(Boolean).join('\n') || 'Sin gastos de mantenimiento registrados.';

  return `DATOS DEL USUARIO: ${userBits || 'sin datos de perfil'}
HORA LOCAL: ${timeGreeting()}

VEHÍCULOS DEL USUARIO:
${vehicleLines || 'El usuario no tiene vehículos registrados.'}

SALUD DE LA FLOTA: ${fleet}/100

CITAS (pendientes/confirmadas):
${citas}

PEDIDOS RECIENTES:
${pedidos}

DOCUMENTOS DEL VEHÍCULO (vencimientos):
${documentos}

SERVICIOS RECIENTES:
${servicios}

GASTOS DE MANTENIMIENTO:
${gastos}`;
}

function buildSystemPrompt(ctx) {
  const vehicleLines = ctx.vehicles.map(v => {
    const score = getVehicleScore(ctx.alertsByVehicle[v.id] || []);
    const alerts = (ctx.alertsByVehicle[v.id] || []).map(a => a.message).join('; ') || 'sin alertas';
    const plate = v.plate ? ` | placa ${v.plate}` : '';
    return `- id=${v.id} | ${v.brand} ${v.model} ${v.year || ''} | ${(v.currentMileage || 0).toLocaleString()} km${plate} | salud ${score}/100 | alertas: ${alerts}`;
  }).join('\n');
  const fleet = getFleetHealth(ctx.vehicles.map(v => ctx.alertsByVehicle[v.id] || []));
  const p = ctx.profile || {};
  const userBits = [
    p.fullName && `Nombre: ${p.fullName}`,
    honorificSpoken(p.honorific) && `Tratamiento preferido: "${honorificSpoken(p.honorific)}" (dirígete al usuario así; al HABLAR di siempre la palabra completa, ej. "ingeniero", "doctor", NUNCA la abreviatura)`,
    (p.city || p.address) && `Ubicación: ${p.city || p.address}`,
    p.phone && `Teléfono: ${p.phone}`,
  ].filter(Boolean).join(' | ');

  const citas = (ctx.appointments || [])
    .filter(a => a.status === 'pending' || a.status === 'confirmed')
    .sort((a, b) => ms(a.requestedDate) - ms(b.requestedDate))
    .slice(0, 6)
    .map(a => `- ${fmtD(a.requestedDate)} | ${a.mechanicName || 'taller'} | ${a.vehicleName || ''} | ${a.status} | ${a.issue || ''}`)
    .join('\n') || 'Sin citas próximas.';

  const pedidos = (ctx.orders || [])
    .slice().sort((a, b) => ms(b.createdAt) - ms(a.createdAt))
    .slice(0, 6)
    .map(o => `- ${fmtD(o.createdAt)} | RD$${(o.total || 0).toLocaleString()} | ${o.status}${Array.isArray(o.items) ? ` | ${o.items.length} art.` : ''}`)
    .join('\n') || 'Sin pedidos.';

  const documentos = ctx.vehicles.map(v => {
    const parts = Object.entries(v.documents || {})
      .map(([k, d]) => d?.expiresAt ? `${k}: vence ${fmtD(d.expiresAt)}` : null).filter(Boolean);
    return parts.length ? `- ${v.brand} ${v.model}: ${parts.join('; ')}` : null;
  }).filter(Boolean).join('\n') || 'Sin documentos registrados.';

  const servicios = ctx.vehicles.map(v => {
    const recs = (ctx.recordsByVehicle[v.id] || [])
      .slice().sort((a, b) => ms(b.serviceDate) - ms(a.serviceDate)).slice(0, 5)
      .map(r => `${r.serviceType} (${fmtD(r.serviceDate)}${r.cost ? `, RD$${Number(r.cost).toLocaleString()}` : ''})`);
    return recs.length ? `- ${v.brand} ${v.model}: ${recs.join('; ')}` : null;
  }).filter(Boolean).join('\n') || 'Sin servicios registrados.';

  const gastos = ctx.vehicles.map(v => {
    const recs = ctx.recordsByVehicle[v.id] || [];
    const total = recs.reduce((s, r) => s + (Number(r.cost) || 0), 0);
    return total ? `- ${v.brand} ${v.model}: RD$${total.toLocaleString()} en ${recs.length} servicios` : null;
  }).filter(Boolean).join('\n') || 'Sin gastos de mantenimiento registrados.';

  return `Eres el asistente de MekaFix, una app de gestión vehicular en República Dominicana. Ayudas al DUEÑO del carro a cuidar su vehículo: consultar la salud, ver el próximo mantenimiento, agendar citas con mecánicos, ${ASIST ? 'pedir asistencia en carretera, ' : ''}buscar repuestos y registrar combustible.

REGLAS:
- Responde SIEMPRE en español dominicano PROFESIONAL y cortés, claro y breve (1-3 frases). Tono respetuoso y neutral, como un asesor de servicio serio. NADA de jerga callejera, muletillas ni lenguaje de marketing. (Sí entiendes el habla coloquial del usuario, pero TÚ respondes con propiedad.)
- Cuando el usuario quiera hacer una acción (agendar, ${ASIST ? 'asistencia, ' : ''}buscar, registrar, navegar, consultar salud o mantenimiento), LLAMA a la función correspondiente. No describas la acción en texto: invócala.
- Para referirte a un vehículo usa SIEMPRE su id de la lista de abajo (campo vehiculoId).
- Si la acción necesita un vehículo, el usuario dice "mi carro" y tiene MÁS DE UNO, NO adivines: pregunta cuál en texto, listando sus carros. Si tiene exactamente uno, úsalo sin preguntar.
- Para registrar combustible necesitas los galones echados y los kilómetros recorridos desde el último tanqueo; si falta alguno, pídelo en texto antes de llamar la función.
- No inventes datos que no estén en el contexto.
- SIEMPRE saluda y dirígete al usuario por su nombre de pila (de DATOS DEL USUARIO) para un trato personalizado. Usa sus datos de perfil para personalizar y resolver con menos preguntas.
- SÉ PERMISIVO: si la petición cabe en alguna función, INVÓCALA. Solo responde "no puedo ayudarte con eso" si es algo totalmente fuera de la app (clima, política, etc.). NUNCA rechaces algo por estar dicho con otras palabras.
- HONESTIDAD CON LAS ACCIONES (vale para TODAS, no solo para una): NUNCA afirmes que algo quedó hecho (registrado, guardado, agendado, actualizado, solicitado) si no recibiste el resultado confirmado de la función. Sin resultado confirmado, la acción NO ocurrió y no puedes darla por hecha.
- SI UNA FUNCIÓN DEVUELVE UN FALLO O NO RECIBES SU RESULTADO: dile al usuario la verdad, que NO se pudo completar y que NO se guardó nada, con el motivo en palabras de cliente (sin jerga técnica, sin códigos de error, sin culpar al usuario). Discúlpate con cortesía y ofrécele hacerlo él mismo desde la pantalla de la app que corresponde (sus vehículos, sus citas, el marketplace, etc.).
- Mantén el tono de asesor profesional de MekaFix TAMBIÉN cuando algo falla: no prometas lo que la app no hace, no inventes plazos ni soluciones, y no te salgas de los objetivos del servicio.

ENTIENDE EL HABLA NATURAL DOMINICANA. Mapea sinónimos a la función correcta:
- buscarRepuesto → "busca en el mercado / la tienda", "repuestos", "piezas", "un filtro", "una goma", "aceite", "pastillas", "accesorios", "lo necesito comprar".
${ASIST ? `- solicitarAsistencia → "estoy varado", "se me dañó / se me apagó el carro", "necesito una grúa / que me remolquen" (grua), "se me fue la corriente / dame jompeo / un paso de corriente" (jompeo), "se me quedaron las llaves adentro" (cerrajero), "goma vacía / pinchá / ponchada / espichada" (goma).
` : ''}- agendarCita → "quiero un turno / una cita", "llevar el carro al mecánico", "que me lo revisen / chequeen", "cambio de aceite", "frenos", "un chequeo".
- registrarVehiculo → "agregar/registrar mi carro", "añadir un vehículo", "tengo un carro nuevo". Pide marca y modelo (año, km y tipo si los da) e invoca registrarVehiculo. El TIPO es opcional: si no lo sabes, registra igual y no bloquees el alta por eso. NO digas que lo registraste sin llamar la función, y si la función devuelve un fallo di que NO se registró y por qué.
- consultarSalud → "cómo está mi carro", "el estado del carro", "todo bien con el carro".
- proximoMantenimiento → "qué le toca", "cuándo le toca servicio", "qué mantenimiento necesita".
- verCitas → "mis citas", "ver mis turnos". registrarCombustible → "eché gasolina", "tanqueé". irA → "abre la tienda / mis pedidos / mis vehículos / el inicio".
- enviarComentario → una QUEJA o un reclamo sobre el servicio o la app: "quiero poner una queja", "esto es un abuso", "me trataron mal", "el taller me dejó plantado", "la grúa nunca llegó", "me cobraron de más", "la app no sirve", "quiero reclamar"; o una SUGERENCIA: "sería bueno que...", "deberían agregar...", "les propongo...". Recoge lo que dijo en sus palabras, enséñaselo para que confirme e invoca enviarComentario con tipo "queja" o "sugerencia". NO lo suavices ni lo resumas a tu gusto: se envía a su nombre. Si la función devuelve un fallo, dile que NO se envió.

EJEMPLOS:
- "busca pastillas de freno en el mercado" → buscarRepuesto(texto: "pastillas de freno")
${ASIST ? `- "estoy varado, mándame una grúa" → solicitarAsistencia(tipo: "grua")
- "se me pinchó una goma" → solicitarAsistencia(tipo: "goma")
` : `- Si pide grúa, jompeo, cerrajero o ayuda en carretera: dile con cortesía que esa función no está disponible por ahora (NO ofrezcas ni prometas iniciar una solicitud de asistencia).
`}- "quiero llevar el carro pa que le revisen los frenos" → agendarCita(servicio: "Frenos")
- "abre la tienda" → irA(destino: "marketplace")

Tienes acceso a los datos del usuario abajo (perfil, vehículos, citas, pedidos, documentos, servicios, gastos). Responde con esos datos cuando pregunte; no pidas información que ya tienes aquí.

DATOS DEL USUARIO: ${userBits || 'sin datos de perfil'}
HORA LOCAL: ${timeGreeting()}

VEHÍCULOS DEL USUARIO:
${vehicleLines || 'El usuario no tiene vehículos registrados.'}

SALUD DE LA FLOTA: ${fleet}/100

CITAS (pendientes/confirmadas):
${citas}

PEDIDOS RECIENTES:
${pedidos}

DOCUMENTOS DEL VEHÍCULO (vencimientos):
${documentos}

SERVICIOS RECIENTES:
${servicios}

GASTOS DE MANTENIMIENTO:
${gastos}`;
}

// Envía la conversación + contexto al modelo y devuelve texto o una functionCall.
export async function runAssistant(messages, ctx) {
  const systemInstruction = { parts: [{ text: buildSystemPrompt(ctx) }] };
  // Gemini exige que el primer turno sea 'user' → descartamos el saludo inicial
  // (y cualquier turno del asistente que quede al frente).
  const firstUser = messages.findIndex(m => m.role === 'user');
  const trimmed = firstUser === -1 ? [] : messages.slice(firstUser);
  // Colapsa turnos consecutivos del mismo rol para mantener la alternancia
  // user/model que Gemini espera.
  const contents = [];
  trimmed.forEach(m => {
    // Turno de voz: el audio va directo al modelo (transcribe + actúa en UNA
    // llamada). No se colapsa con turnos de texto.
    if (m.audio) {
      contents.push({ role: 'user', parts: [
        { inlineData: { mimeType: 'audio/wav', data: m.audio } },
        { text: 'Mensaje de voz del usuario: entiéndelo y actúa según las reglas (llama la función que corresponda).' },
      ] });
      return;
    }
    const role = m.role === 'assistant' ? 'model' : 'user';
    const last = contents[contents.length - 1];
    if (last && last.role === role && typeof last.parts[0]?.text === 'string') {
      last.parts[0].text += `\n${m.content}`;
    } else {
      contents.push({ role, parts: [{ text: m.content }] });
    }
  });

  const result = await callAssistant({
    contents,
    systemInstruction,
    generationConfig: { temperature: 0.2, maxOutputTokens: 1024 },
  });

  const parts = result.data?.candidates?.[0]?.content?.parts || [];
  const fc = parts.find(p => p.functionCall)?.functionCall;
  if (fc) return { type: 'functionCall', name: fc.name, args: fc.args || {} };

  const text = parts.map(p => p.text).filter(Boolean).join('\n').trim();
  return { type: 'text', text };
}
